import { CommonModule, NgIf } from "@angular/common";
import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";

import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { DropdownPosition, NgSelectModule } from "@ng-select/ng-select";
import { Observable, Subject, debounceTime, distinctUntilChanged } from "rxjs";
import { ModalComponent } from "../../../components/layout/modal/modal.component";
import { SelectOption } from "../../../models/option";
import { FormControlElement } from "../../models/form-control-element";
import { IsRequiredPipe } from "../../pipes/is-required.pipe";
import { HorizontalWrapperComponent } from "../horizontal-wrapper/horizontal-wrapper.component";
import { ValidatorComponent } from "../validator/validator.component";

@Component({
  selector: "app-select",
  templateUrl: "./select.component.html",
  styleUrls: ["./select.component.scss"],
  standalone: true,
  imports: [
    NgIf,
    HorizontalWrapperComponent,
    NgSelectModule,
    ReactiveFormsModule,
    ValidatorComponent,
    IsRequiredPipe,
    CommonModule,
    ModalComponent,
  ],
})
export class SelectComponent extends FormControlElement implements OnInit, OnChanges {
  @Input() type: string = "";
  @Input() alignRight: boolean = false;
  @Input() marginBottom: boolean = true;
  @Input() id: string = "";
  @Input() inTable: boolean = false; // zit de keuzelijst in een tabel vervat of niet?
  @Input() isReadOnly: boolean = false;

  @Input() modal: boolean = false; // toon modal met meer info
  @Input() modalTitle: string = "Meer info";
  @Input() modalInfo!: HTMLDivElement;
  @Output() infoClicked = new EventEmitter();
  @ViewChild("content") content!: ElementRef;
  @ContentChild("optionTemplate", { read: TemplateRef }) optionTemplate?: TemplateRef<any>;
  @ContentChild("labelTemplate", { read: TemplateRef }) labelTemplate?: TemplateRef<any>;

  @Input() options: SelectOption[] | Observable<SelectOption[]> = []; // de opties die kunnen gekozen worden
  options2: SelectOption[] = [{ id: "null", name: "Bezig met laden...", disabled: true }]; // de opties die kunnen gekozen worden
  @Input() label: string = "";
  @Input() placeholder: string = "";
  @Input() notAddEmpty: boolean = false; // er moet geen leeg item met '---' toegevoged worden. //TODO: dead code
  @Input() showWhenEmpty: boolean = true;
  @Input() clearable: boolean = true;
  @Input() virtualScroll: boolean = false;
  @Input() autoSelectIfPossible = false;
  // Lazy-laden bij grote lijsten: bij mount enkel het geselecteerde item (voor het
  // label), de volledige lijst pas wanneer de dropdown opent. Scheelt het bouwen van
  // een ng-select-itemsList van duizenden items per instantie (bv. de contactenlijst).
  @Input() lazyOptions = false;
  private fullOptionsSource: SelectOption[] = [];
  private lazyExpanded = false;

  @Input() showAdd: boolean = false; // toon de toevoegen knop
  @Input() showEdit: boolean = false; // toon de edit knop
  @Input() addTitle: string = "Toevoegen"; // toon de toevoegen knop
  @Input() editTitle: string = "Bewerken"; // toon de edit knop
  @Input() loading = false;

  @Output() onAdd = new EventEmitter<any>(); // functie om de keuze te bewaren
  @Output() onEdit = new EventEmitter<any>(); // functie om de keuze te bewaren
  @Output() onSearch = new EventEmitter<any>(); // event om de lijst met items eventueel te updaten.
  @Output() onClear = new EventEmitter<any>();
  @Output() onChange = new EventEmitter<any>();

  @Input() showDetails: boolean = false; // toon de tonen knop
  @Input() detailsTitle: string = "Bekijk details"; // toon de tonen knop

  @Input() appendTo!: string; // de id van het element waar de dropdown in moet komen
  @Input() dropdownPosition: DropdownPosition = "auto";

  @Input() hideSelectedOption = false;

  input$ = new Subject<string>();

  isObservable = false;
  options$!: Observable<SelectOption[]>;
  /**
   * @deprecated
   * Should be removed, or default to false
   */
  @Input() convertString: boolean = true;

  readOnlyValue: string | undefined = "";

  config: any;
  constructor(
    private modalService: NgbModal,
    private elementRef: ElementRef
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.options instanceof Observable) {
      this.isObservable = true;
      this.options$ = this.options as Observable<SelectOption[]>;
      this.options.subscribe((options: SelectOption[]) => this.setOptions2(options));
    } else {
      this.setOptions2(this.options);
    }

    if (this.onSearch.observers.length > 0)
      this.input$.pipe(debounceTime(500), distinctUntilChanged()).subscribe((x) => {
        if (this.onSearch && this.onSearch.observers && this.onSearch.observers.length > 0) {
          this.onSearch.emit(x);
        }
      });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["options"]) {
      let options = changes["options"];
      if (options.currentValue != options.previousValue) {
        this.updateCurrent();
      }
    } else if (changes["control"]) {
      this.loadIn();
    }
  }

  updateOptions(options: SelectOption[] | Observable<SelectOption[]>) {
    this.options = options;
    if (this.options instanceof Observable) {
      this.isObservable = true;
      this.options$ = this.options as Observable<SelectOption[]>;
      this.options.subscribe((options: SelectOption[]) => this.setOptions2(options));
    } else {
      this.setOptions2(this.options);
    }
  }

  // Bouwt options2 uit de bronlijst. In lazy-modus (en zolang de dropdown nog niet
  // geopend is) zetten we enkel het geselecteerde item; de volledige lijst (en dus de
  // zware ng-select-itemsList) komt pas bij onLazyOpen().
  private setOptions2(source: SelectOption[]): void {
    if (this.lazyOptions && !this.lazyExpanded) {
      this.fullOptionsSource = source;
      this.options2 = this.selectedOnly(source);
    } else {
      this.options2 = source.map((x) => {
        x.id = this.convertString ? x.id.toString() : x.id;
        return x;
      });
    }
    this.loadIn();
  }

  private selectedOnly(source: SelectOption[]): SelectOption[] {
    const val = this.control?.value;
    if (val == null || val === "") return [];
    const sel = source.find((o) => (this.convertString ? o.id?.toString() : o.id) == val);
    if (!sel) return [];
    sel.id = this.convertString ? sel.id.toString() : sel.id;
    return [sel];
  }

  // Bij het openen van de dropdown: laad alsnog de volledige lijst (eenmalig).
  onLazyOpen(): void {
    if (this.lazyOptions && !this.lazyExpanded && this.fullOptionsSource.length) {
      this.lazyExpanded = true;
      this.options2 = this.fullOptionsSource.map((x) => {
        x.id = this.convertString ? x.id.toString() : x.id;
        return x;
      });
    }
  }

  updateCurrent() {
    this.updateOptions(this.options);
  }

  loadIn() {
    if (this.control.value != null && this.control.value !== "") {
      this.control.patchValue(
        this.convertString ? this.control.value.toString() : this.control.value,
        { emitEvent: false }
      );
    } else if (!this.showAdd && this.options2.length == 1 && this.autoSelectIfPossible) {
      this.control.patchValue(this.options2[0].id);
    }
    this.readOnlyValue = this.options2.find((x) => x.id == this.control.value)?.name;
    if (!this.readOnlyValue) {
      this.readOnlyValue = `Geen ${this.label.toLowerCase()} geselecteerd`;
    }
  }

  addNew() {
    if (this.onAdd && this.onAdd.observers && this.onAdd.observers.length > 0) {
      this.onAdd.emit();
    } else {
      this.control.patchValue(null);
    }
  }

  edit() {
    if (this.onEdit && this.onEdit.observers && this.onEdit.observers.length > 0) {
      this.onEdit.emit();
    }
  }

  change(event: any) {
    if (this.onChange && this.onChange.observed) {
      this.onChange.emit(event);
    }
  }

  open(content: any) {
    if (this.infoClicked.observed) {
      this.infoClicked.emit();
    } else {
      this.modalService.open(content, {
        ariaLabelledBy: "modal-basic-title",
        centered: true,
        size: "lg",
      });
    }
  }

  onRemove(event: any) {}

  onClearClick() {
    this.onClear.emit();
  }

  /*
  search(event: any) {
    //if (this.onSearch && this.onSearch.observers && this.onSearch.observers.length > 0) {
    //  this.onSearch.emit(event);
    //}
  }*/

  get isInModal() {
    // If appendTo is explicitly set, don't override it
    if (this.appendTo) {
      return false;
    }
    let parent = this.elementRef.nativeElement.parentElement;
    while (parent) {
      if (parent.tagName == "NGB-MODAL-WINDOW" || parent.classList.contains("modal-content")) {
        return true;
      }
      parent = parent.parentElement;
    }
    return false;
  }

  identity(arg: SelectOption): SelectOption {
    return arg;
  }
}
